import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box } from '@mui/material';
import EditableList from '../EditableList/EditableList';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import { Button } from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOn';
import ListIcon from '@mui/icons-material/List';
import { useNavigate } from 'react-router-dom';

function RoleManagement() {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();

  if (!authCtx.checkRight('role:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const canSeeRightsOverview = authCtx.checkRight('role:read') && authCtx.checkRight('user:read');

  // We'll manage roles and their assigned rights; we expose flat arrays per device/level
  const roleSchema = {
    name: {
      label: 'Name',
      api: { itemField: 'name' },
      placeholder: 'Interner Name',
      type: 'text',
      required: true,
      minWidth: '160px',
    },
    readableName: {
      label: 'Anzeigename',
      api: { itemField: 'readableName' },
      placeholder: 'z. B. Filialleiter',
      type: 'text',
      required: true,
      minWidth: '200px',
    },
    description: {
      label: 'Beschreibung',
      api: { itemField: 'description' },
      placeholder: '-',
      type: 'text',
      required: false,
      minWidth: '260px',
    },
    webauthnMandatory: {
      label: 'Passkey Pflicht',
      api: { itemField: 'webauthnMandatory' },
      type: 'boolean',
      required: false,
      minWidth: '140px',
      transformValue: value => value === true || value === 'true',
    },
    webauthnGracePeriodDays: {
      label: 'Grace (Tage)',
      api: { itemField: 'webauthnGracePeriodDays' },
      type: 'number',
      required: false,
      minWidth: '130px',
      transformValue: value => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 14;
      },
    },
    webauthnEnforceSecurityKeyOnly: {
      label: 'Nur Security Key',
      api: { itemField: 'webauthnEnforceSecurityKeyOnly' },
      type: 'boolean',
      required: false,
      minWidth: '160px',
      transformValue: value => value === true || value === 'true',
    },
    companyStandard: {
      label: 'Firma (Standard)',
      type: 'multi-select',
      api: {
        fieldName: 'right',
        itemField: 'companyStandard',
        resource: 'right/strings',
        skipSpecial: true,
      },
      placeholder: 'Rechte auswählen',
      required: false,
      minWidth: '260px',
      readOnly: true,
      transformValue: (selectedValues, options) => {
        const array = Array.isArray(selectedValues)
          ? selectedValues
          : [selectedValues].filter(Boolean);
        return array
          .map(v => (typeof v === 'object' && v !== null ? (v.value ?? '') : v))
          .filter(Boolean);
      },
    },
    companyExpanded: {
      label: 'Firma (Erweitert)',
      type: 'multi-select',
      api: {
        fieldName: 'right',
        itemField: 'companyExpanded',
        resource: 'right/strings',
        skipSpecial: true,
      },
      placeholder: 'Rechte auswählen',
      readOnly: true,
      transformValue: (selectedValues, options) => {
        const array = Array.isArray(selectedValues)
          ? selectedValues
          : [selectedValues].filter(Boolean);
        return array
          .map(v => (typeof v === 'object' && v !== null ? (v.value ?? '') : v))
          .filter(Boolean);
      },
    },
    counterStandard: {
      label: 'Kasse (Standard)',
      type: 'multi-select',
      api: {
        fieldName: 'right',
        itemField: 'counterStandard',
        resource: 'right/strings',
        skipSpecial: true,
      },
      placeholder: 'Rechte auswählen',
      readOnly: true,
      transformValue: (selectedValues, options) => {
        const array = Array.isArray(selectedValues)
          ? selectedValues
          : [selectedValues].filter(Boolean);
        return array
          .map(v => (typeof v === 'object' && v !== null ? (v.value ?? '') : v))
          .filter(Boolean);
      },
    },
    counterExpanded: {
      label: 'Kasse (Erweitert)',
      type: 'multi-select',
      api: {
        fieldName: 'right',
        itemField: 'counterExpanded',
        resource: 'right/strings',
        skipSpecial: true,
      },
      placeholder: 'Rechte auswählen',
      readOnly: true,
      transformValue: (selectedValues, options) => {
        const array = Array.isArray(selectedValues)
          ? selectedValues
          : [selectedValues].filter(Boolean);
        return array
          .map(v => (typeof v === 'object' && v !== null ? (v.value ?? '') : v))
          .filter(Boolean);
      },
    },
    privateStandard: {
      label: 'Privat (Standard)',
      type: 'multi-select',
      api: {
        fieldName: 'right',
        itemField: 'privateStandard',
        resource: 'right/strings',
        skipSpecial: true,
      },
      placeholder: 'Rechte auswählen',
      readOnly: true,
      transformValue: (selectedValues, options) => {
        const array = Array.isArray(selectedValues)
          ? selectedValues
          : [selectedValues].filter(Boolean);
        return array
          .map(v => (typeof v === 'object' && v !== null ? (v.value ?? '') : v))
          .filter(Boolean);
      },
    },
    privateExpanded: {
      label: 'Privat (Erweitert)',
      type: 'multi-select',
      api: {
        fieldName: 'right',
        itemField: 'privateExpanded',
        resource: 'right/strings',
        skipSpecial: true,
      },
      placeholder: 'Rechte auswählen',
      readOnly: true,
      transformValue: (selectedValues, options) => {
        const array = Array.isArray(selectedValues)
          ? selectedValues
          : [selectedValues].filter(Boolean);
        return array
          .map(v => (typeof v === 'object' && v !== null ? (v.value ?? '') : v))
          .filter(Boolean);
      },
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    <EditableList
      schema={roleSchema}
      title='Neue Rolle anlegen'
      resourceType='role'
      headerSearchPlaceholder='Rollen durchsuchen'
      parentIsEditable={authCtx.checkRight('role:update')}
      options={{ titleKey: 'readableName' }}
      renderRowActions={item => (
        <>
          <Button
            size='small'
            variant='outlined'
            startIcon={<GridOnIcon />}
            onClick={() => navigate(`/roleRightsMatrix?roleId=${item._id}`)}
            sx={{ textTransform: 'none', mr: 1 }}
          >
            Rechte-Matrix
          </Button>
          {canSeeRightsOverview && (
            <Button
              size='small'
              variant='outlined'
              startIcon={<ListIcon />}
              onClick={() => navigate('/roleRightsOverview')}
              sx={{ textTransform: 'none' }}
            >
              Rechte-Übersicht
            </Button>
          )}
        </>
      )}
      visibleColumnKeys={[
        'name',
        'readableName',
        'description',
        'webauthnMandatory',
        'webauthnGracePeriodDays',
        'webauthnEnforceSecurityKeyOnly',
      ]}
    />
    </Box>
  );
}

export default RoleManagement;
